import React, { useEffect, useState } from 'react';
import { useReducedMotion } from 'framer-motion';

/**
 * Hook returning a string that types and deletes through `phrases`.
 * Returns the static `fallback` (or the first phrase) when paused or reduced motion is on.
 */
export function useTypewriter(phrases = [], { paused = false, fallback = '', prefix = '', typeMs = 55, deleteMs = 26, holdMs = 1700 } = {}) {
  const reduce = useReducedMotion();
  const [text, setText] = useState('');
  const [idx, setIdx] = useState(0);
  const [deleting, setDeleting] = useState(false);
  const enabled = phrases.length > 0 && !paused && !reduce;
  const phrase = phrases.length ? phrases[idx % phrases.length] : '';

  useEffect(() => {
    if (!enabled) return undefined;
    let t;
    if (!deleting && text === phrase) t = setTimeout(() => setDeleting(true), holdMs);
    else if (deleting && text === '') {
      t = setTimeout(() => {
        setDeleting(false);
        setIdx((i) => (i + 1) % phrases.length);
      }, 300);
    } else {
      t = setTimeout(() => setText(deleting ? phrase.slice(0, text.length - 1) : phrase.slice(0, text.length + 1)), deleting ? deleteMs : typeMs);
    }
    return () => clearTimeout(t);
  }, [enabled, text, deleting, phrase, phrases.length, typeMs, deleteMs, holdMs]);

  if (!enabled) return fallback || (phrases[0] ? `${prefix}${phrases[0]}` : '');
  return `${prefix}${text}`;
}

/** Decorative component form with a blinking caret. `<TypewriterPlaceholder phrases={[...]} />` */
export default function TypewriterPlaceholder({ phrases, prefix = '', className = '' }) {
  const text = useTypewriter(phrases, { prefix });
  return (
    <span className={className} aria-hidden="true">
      {text}
      <span className="ml-0.5 inline-block h-[1em] w-[2px] animate-blink bg-current align-middle" />
    </span>
  );
}
